import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, FolderKanban, Users, UserPlus, X, Calendar,
  MapPin, Building2, CheckCircle, Clock, AlertCircle, RefreshCw,
  Link2, Unlink, Settings2
} from 'lucide-react';
import api from '../../services/apiService';
import SuspendedCompanyOverlay from './shared/SuspendedCompanyOverlay';

const WindCompanyProjectDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [assignments, setAssignments] = useState([]);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableAuthorizations, setAvailableAuthorizations] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [assigningId, setAssigningId] = useState(null);

  // Project Mapping state
  const [projectMapping, setProjectMapping] = useState(null);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [availableWindProjects, setAvailableWindProjects] = useState([]);
  const [loadingWindProjects, setLoadingWindProjects] = useState(false);
  const [mappingInProgress, setMappingInProgress] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const projectResponse = await api.get(`/oem/projects/${id}/`);
      setProject(projectResponse.data);

      const assignmentsResponse = await api.get(`/oem/project-assignments/?project=${id}`);
      setAssignments(assignmentsResponse.data);

      // Fetch project mapping for this OEM project
      try {
        const mappingResponse = await api.get(`/oem/project-mappings/?oem_project=${id}`);
        const mappings = mappingResponse.data?.results || mappingResponse.data || [];
        // Find the mapping for current user's company (should be only one)
        const myMapping = mappings.find(m => m.status === 'active');
        setProjectMapping(myMapping || null);
      } catch (mappingErr) {
        console.log('No mapping found or error:', mappingErr);
        setProjectMapping(null);
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      if (err.response?.status === 404) {
        setError('Project not found');
      } else if (err.response?.status === 403) {
        setError('You do not have access to this project');
      } else {
        setError('Failed to load project details');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const fetchAvailableForProject = async () => {
    setShowAssignModal(true);
    setLoadingAvailable(true);
    try {
      const response = await api.get(`/oem/projects/${id}/available-authorizations/`);
      setAvailableAuthorizations(response.data.authorizations || []);
    } catch (err) {
      console.error('Error fetching available authorizations:', err);
      setAvailableAuthorizations([]);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleAssignToProject = async (authorization) => {
    if (assigningId) return;

    setAssigningId(authorization.id);
    try {
      await api.post(`/oem/projects/${id}/assignments/`, {
        authorization: authorization.id
      });

      setAvailableAuthorizations(prev =>
        prev.filter(a => a.id !== authorization.id)
      );

      const assignmentsResponse = await api.get(`/oem/project-assignments/?project=${id}`);
      setAssignments(assignmentsResponse.data);
    } catch (err) {
      console.error('Error assigning technician:', err);
      alert(err.response?.data?.error || err.response?.data?.detail || 'Failed to assign technician');
    } finally {
      setAssigningId(null);
    }
  };

  // Project Mapping functions
  const openMappingModal = async () => {
    setShowMappingModal(true);
    setLoadingWindProjects(true);
    try {
      const response = await api.get(`/oem/project-mappings/available-wind-projects/?oem_project=${id}`);
      setAvailableWindProjects(response.data || []);
    } catch (err) {
      console.error('Error fetching wind projects:', err);
      setAvailableWindProjects([]);
    } finally {
      setLoadingWindProjects(false);
    }
  };

  const handleCreateMapping = async (windProject) => {
    if (mappingInProgress) return;
    setMappingInProgress(true);
    try {
      await api.post('/oem/project-mappings/', {
        oem_project: parseInt(id),
        wind_project: windProject.id
      });
      setShowMappingModal(false);
      fetchData(); // Refresh to show the new mapping
    } catch (err) {
      console.error('Error creating mapping:', err);
      alert(err.response?.data?.detail || err.response?.data?.error || 'Failed to create mapping');
    } finally {
      setMappingInProgress(false);
    }
  };

  const handleRemoveMapping = async () => {
    if (!projectMapping) return;
    if (!window.confirm('Are you sure you want to remove the project mapping? All your technicians with OEM authorizations will become visible in this project again.')) {
      return;
    }
    try {
      await api.delete(`/oem/project-mappings/${projectMapping.id}/`);
      setProjectMapping(null);
      fetchData(); // Refresh
    } catch (err) {
      console.error('Error removing mapping:', err);
      alert(err.response?.data?.detail || 'Failed to remove mapping');
    }
  };

  const getStatusStyle = (status) => {
    const styles = {
      active: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
      suspended: { bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-500' },
      completed: { bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-500' },
      removed: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
    };
    return styles[status] || styles.active;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-600">Loading project...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md text-center">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Error</h2>
          <p className="text-slate-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/oem-company/projects')}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Back to projects
          </button>
        </div>
      </div>
    );
  }

  // Project Mapping Modal
  const MappingModal = () => {
    if (!showMappingModal) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        onClick={() => setShowMappingModal(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Link Resource Board Project</h3>
              <p className="text-sm text-slate-500">Select your project to pair with this OEM project</p>
            </div>
            <button
              onClick={() => setShowMappingModal(false)}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>

          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {loadingWindProjects ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                <span className="ml-2 text-slate-600">Loading your projects...</span>
              </div>
            ) : availableWindProjects.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FolderKanban className="h-7 w-7 text-slate-400" />
                </div>
                <p className="text-slate-600 mb-2">No projects available</p>
                <p className="text-sm text-slate-400">You don't have any projects in Resource Board to link.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-500 mb-4">
                  By linking your Resource Board project, only technicians planned on that project will be visible in this OEM project.
                </p>
                {availableWindProjects.map((proj) => (
                  <div
                    key={proj.id}
                    className="p-4 rounded-xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50/30 transition-all cursor-pointer"
                    onClick={() => handleCreateMapping(proj)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <FolderKanban className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{proj.name}</p>
                          <p className="text-sm text-slate-500">{proj.code || `ID: ${proj.id}`}</p>
                        </div>
                      </div>
                      <button
                        disabled={mappingInProgress}
                        className="h-9 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700
                                 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium transition-colors"
                      >
                        {mappingInProgress ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Link2 className="h-4 w-4" />
                            Link
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-100 flex justify-end bg-slate-50">
            <button
              onClick={() => setShowMappingModal(false)}
              className="h-9 px-4 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const AssignModal = () => {
    if (!showAssignModal) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        onClick={() => setShowAssignModal(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Assign Technicians</h3>
              <p className="text-sm text-slate-500">{project?.code} - {project?.name}</p>
            </div>
            <button
              onClick={() => setShowAssignModal(false)}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>

          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {loadingAvailable ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="ml-2 text-slate-600">Loading...</span>
              </div>
            ) : availableAuthorizations.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-7 w-7 text-slate-400" />
                </div>
                <p className="text-slate-600 mb-2">No available technicians</p>
                <p className="text-sm text-slate-400 mb-4">All authorized technicians have been assigned.</p>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    navigate('/oem-company/nominations/create');
                  }}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Create a nomination →
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {availableAuthorizations.map((auth) => (
                  <div
                    key={auth.id}
                    className="p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {auth.profile_picture ? (
                          <img
                            src={auth.profile_picture}
                            alt={auth.user_name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold">
                            {auth.user_name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-slate-900">{auth.user_name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span
                              className="inline-block px-2 py-0.5 text-xs font-semibold rounded text-white"
                              style={{ backgroundColor: auth.authorization_level_color || '#3B82F6' }}
                            >
                              {auth.authorization_level_code}
                            </span>
                            <span className="text-xs text-slate-500">
                              Valid: {formatDate(auth.valid_from)} → {formatDate(auth.valid_until)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAssignToProject(auth)}
                        disabled={assigningId === auth.id}
                        className="h-9 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                                 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium transition-colors"
                      >
                        {assigningId === auth.id ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="h-4 w-4" />
                            Assign
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-100 flex justify-end bg-slate-50">
            <button
              onClick={() => setShowAssignModal(false)}
              className="h-9 px-4 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <SuspendedCompanyOverlay>
      <div className="min-h-screen bg-slate-50">
        <div className="p-6 max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/oem-company/projects')}
                className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{project?.code}</h1>
                <p className="text-slate-500">{project?.name}</p>
              </div>
            </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4 text-slate-600" />
            </button>
            <button
              onClick={fetchAvailableForProject}
              className="h-10 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium transition-colors"
            >
              <UserPlus className="h-4 w-4" />
              Assign Technicians
            </button>
          </div>
        </div>

        {/* Project Info Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-4 bg-gradient-to-r from-slate-50 to-white">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <FolderKanban className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-900">{project?.code}</h2>
                <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                  project?.status === 'active'
                    ? 'bg-emerald-50 text-emerald-600'
                    : project?.status === 'completed'
                    ? 'bg-blue-50 text-blue-600'
                    : 'bg-slate-100 text-slate-500'
                }`}>
                  {project?.status}
                </span>
              </div>
              <p className="text-slate-500 mt-0.5">{project?.name}</p>
            </div>
          </div>

          <div className="p-6 grid grid-cols-4 gap-6">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">OEM Company</label>
              <div className="flex items-center gap-2 mt-2">
                <Building2 className="h-4 w-4 text-slate-400" />
                <span className="text-slate-900">{project?.oem_company_name || 'N/A'}</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Location</label>
              <div className="flex items-center gap-2 mt-2">
                <MapPin className="h-4 w-4 text-slate-400" />
                <span className="text-slate-900">{project?.location || 'N/A'}</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Start Date</label>
              <div className="flex items-center gap-2 mt-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span className="text-slate-900">{formatDate(project?.start_date)}</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">End Date</label>
              <div className="flex items-center gap-2 mt-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span className="text-slate-900">{formatDate(project?.end_date)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Resource Board Mapping Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Link2 className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Resource Board Mapping</h3>
                <p className="text-xs text-slate-500">Link your internal project to filter visible technicians</p>
              </div>
            </div>
            {projectMapping ? (
              <button
                onClick={handleRemoveMapping}
                className="h-9 px-4 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 flex items-center gap-2 text-sm font-medium transition-colors"
              >
                <Unlink className="h-4 w-4" />
                Remove Link
              </button>
            ) : (
              <button
                onClick={openMappingModal}
                className="h-9 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium transition-colors"
              >
                <Link2 className="h-4 w-4" />
                Link Project
              </button>
            )}
          </div>

          <div className="p-6">
            {projectMapping ? (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-purple-50 border border-purple-100">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <FolderKanban className="h-6 w-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-purple-700">Linked to:</span>
                    <span className="text-sm font-bold text-slate-900">{projectMapping.wind_project_name}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    Only technicians planned on this project in your Resource Board are visible here.
                    {projectMapping.technicians_count !== undefined && (
                      <span className="font-medium text-purple-600"> ({projectMapping.technicians_count} technicians planned)</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-emerald-100 text-emerald-700">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Active
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Link2 className="h-7 w-7 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No project linked</h3>
                <p className="text-slate-500 mb-4 max-w-md mx-auto">
                  Link one of your Resource Board projects to this OEM project.
                  This will filter which of your technicians are visible here - only those planned on the linked project.
                </p>
                <button
                  onClick={openMappingModal}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  Link a project now →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Assigned Technicians */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users className="h-4 w-4 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Assigned Technicians</h3>
            </div>
            <span className="text-sm text-slate-500">{assignments.length} assigned</span>
          </div>

          <div className="p-6">
            {assignments.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-7 w-7 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No technicians assigned</h3>
                <p className="text-slate-500 mb-4">This project doesn't have any technicians yet.</p>
                <button
                  onClick={fetchAvailableForProject}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Assign technicians now →
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {assignments.map((assignment) => {
                  const style = getStatusStyle(assignment.status);
                  return (
                    <div
                      key={assignment.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        {assignment.profile_picture ? (
                          <img
                            src={assignment.profile_picture}
                            alt={assignment.user_name}
                            className="w-11 h-11 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-semibold">
                            {assignment.user_name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-slate-900">{assignment.user_name}</p>
                          <p className="text-sm text-slate-500">{assignment.user_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span
                            className="inline-block px-2.5 py-1 text-xs font-bold rounded text-white"
                            style={{ backgroundColor: assignment.authorization_level_color || '#3B82F6' }}
                          >
                            {assignment.authorization_level_code}
                          </span>
                          <p className="text-xs text-slate-500 mt-1">{assignment.authorization_level_name}</p>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg ${style.bg} ${style.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                          {assignment.status}
                        </span>
                        <div className="text-right text-sm text-slate-400 min-w-[100px]">
                          <p>Assigned</p>
                          <p className="font-medium text-slate-600">{formatDate(assignment.assigned_at)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <AssignModal />
        <MappingModal />
      </div>
    </div>
    </SuspendedCompanyOverlay>
  );
};

export default WindCompanyProjectDetail;
